import { ModalLayout } from "moh-ui";
import React, { useState } from "react";
import { useRecoilState } from "recoil";
import { showQuestionAuditState } from "@/atoms";
import { useGetQuestionActions } from "@/services/questions";
import { QuestionAction } from "@/types/examination.types";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Text,
  Stack,
  Card,
  LoadingOverlay,
} from "@mantine/core";
import { Column, TableWrapper } from "moh-ui";
import { LuDownload, LuRefreshCw, LuEye } from "react-icons/lu";
import { downloadQuestionActionsExport } from "@/services/questions";
import { notifications } from "@mantine/notifications";

const actionTypeColors: Record<string, string> = {
  CREATE: "green",
  UPDATE: "blue",
  DELETE: "red",
  RESTORE: "yellow",
  BULK_CREATE: "green",
  BULK_UPDATE: "blue",
  BULK_DELETE: "red",
} as const;

const columns: Column<QuestionAction>[] = [
  {
    title: "Action Type",
    key: "actionType",
    Element: ({ row }) => (
      <Badge color={actionTypeColors[row.actionType] || "gray"}>
        {row.actionType}
      </Badge>
    ),
    sortable: true,
  },
  {
    title: "Author",
    key: "authorName",
    Element: ({ row }) => (
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          {row.authorName}
        </Text>
        <Text size="xs" c="dimmed">
          {row.authorEmail}
        </Text>
      </Stack>
    ),
    sortable: true,
  },
  {
    title: "Reason",
    key: "reason",
    Element: ({ row }) => (
      <Text size="sm" lineClamp={2}>
        {row.reason}
      </Text>
    ),
  },
  {
    title: "Timestamp",
    key: "actionTimestamp",
    Element: ({ row }) => (
      <Text size="sm">{new Date(row.actionTimestamp).toLocaleString()}</Text>
    ),
    sortable: true,
  },
  {
    title: "Actions",
    key: "actions",
    Element: ({ row }) => {
      const [showDetails, setShowDetails] = useState(false);

      return (
        <Group gap="xs">
          <ActionIcon
            size="sm"
            variant="light"
            onClick={() => setShowDetails(!showDetails)}
          >
            <LuEye />
          </ActionIcon>
          {showDetails && (
            <Card p="xs" withBorder>
              <Stack gap="xs">
                {row.oldData && (
                  <div>
                    <Text size="xs" fw={500} c="dimmed">
                      Previous Data:
                    </Text>
                    <Text
                      size="xs"
                      style={{ maxWidth: "200px", wordBreak: "break-word" }}
                    >
                      {row.oldData}
                    </Text>
                  </div>
                )}
                {row.newData && (
                  <div>
                    <Text size="xs" fw={500} c="dimmed">
                      New Data:
                    </Text>
                    <Text
                      size="xs"
                      style={{ maxWidth: "200px", wordBreak: "break-word" }}
                    >
                      {row.newData}
                    </Text>
                  </div>
                )}
                {row.additionalData && (
                  <div>
                    <Text size="xs" fw={500} c="dimmed">
                      Additional Data:
                    </Text>
                    <Text
                      size="xs"
                      style={{ maxWidth: "200px", wordBreak: "break-word" }}
                    >
                      {JSON.stringify(row.additionalData)}
                    </Text>
                  </div>
                )}
              </Stack>
            </Card>
          )}
        </Group>
      );
    },
  },
];

export default function QuestionAuditsModal() {
  const [show, setShow] = useRecoilState(showQuestionAuditState);
  const [exportFormat, setExportFormat] = useState<"csv" | "excel">("excel");
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading, refetch } = useGetQuestionActions(
    show?.question?.id || "",
    Boolean(show?.show)
  );

  const actions = data?.data || [];

  const handleExport = async () => {
    if (!show?.question?.id) return;

    setIsExporting(true);
    try {
      const filters = {
        questionId: show.question.id,
        councilId: show.question.council?.id,
      };

      await downloadQuestionActionsExport(exportFormat, filters);

      notifications.show({
        title: "Export Successful",
        message: `Question actions exported as ${exportFormat.toUpperCase()}`,
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Export Failed",
        message: "Failed to export question actions",
        color: "red",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <ModalLayout
      open={Boolean(show?.show)}
      onClose={() => setShow(null)}
      size="xl"
    >
      <div className="p-6">
        <Stack gap="lg">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <Text size="xl" fw={600}>
                Question Audit Trail
              </Text>
              <Text size="sm" c="dimmed">
                Action history for:{" "}
                {show?.question?.translations?.EN ||
                  "No question text available"}
              </Text>
            </div>

            <Group gap="xs">
              <ActionIcon
                size="lg"
                onClick={() => refetch()}
                loading={isLoading}
              >
                <LuRefreshCw />
              </ActionIcon>

              <Group gap="xs">
                <select
                  value={exportFormat}
                  onChange={(e) =>
                    setExportFormat(e.target.value as "csv" | "excel")
                  }
                  className="px-3 py-2 border rounded-md text-sm"
                >
                  <option value="excel">Excel</option>
                  <option value="csv">CSV</option>
                </select>

                <Button
                  size="sm"
                  leftSection={<LuDownload />}
                  onClick={handleExport}
                  loading={isExporting}
                  disabled={actions.length === 0}
                >
                  Export
                </Button>
              </Group>
            </Group>
          </div>

          {/* Content */}
          <div className="relative">
            <LoadingOverlay visible={isLoading} />

            {actions.length === 0 && !isLoading ? (
              <Card p="xl" withBorder>
                <Text ta="center" c="dimmed">
                  No action history found for this question.
                </Text>
              </Card>
            ) : (
              <TableWrapper
                data={actions}
                columns={columns}
                loading={isLoading}
              />
            )}
          </div>

          {/* Summary */}
          {actions.length > 0 && (
            <Card p="md" withBorder>
              <Group gap="lg">
                <div>
                  <Text size="sm" fw={500}>
                    Total Actions:
                  </Text>
                  <Text size="lg" fw={600}>
                    {actions.length}
                  </Text>
                </div>

                <div>
                  <Text size="sm" fw={500}>
                    Latest Action:
                  </Text>
                  <Text size="sm">
                    {actions[0]?.actionType} by {actions[0]?.authorName}
                  </Text>
                </div>

                <div>
                  <Text size="sm" fw={500}>
                    First Action:
                  </Text>
                  <Text size="sm">
                    {actions[actions.length - 1]?.actionType} by{" "}
                    {actions[actions.length - 1]?.authorName}
                  </Text>
                </div>
              </Group>
            </Card>
          )}
        </Stack>
      </div>
    </ModalLayout>
  );
}
